import { useEffect, useState } from 'react'
import { api, ApiError } from '../lib/api'
import { usePageTitle } from '../hooks/usePageTitle'
import {
  ClubButton,
  ClubLinkButton,
  ClubPage,
  Container,
  EmptyPlate,
  Eyebrow,
} from '../components/golf'
import { formatCents } from '../lib/clubMoney'
import type { InviteResponseChoice, InviteView, InviteViewResponse } from '../lib/types'

/**
 * An outing invitation (`/invites/:token`) — PUBLIC and tokenized: the invitee
 * arrives from an email with no account and answers in two taps. The token is
 * the whole identity; the page asks for no sign-in of its own, and wears the
 * `invite` chrome (public header, no Apply pill) so "I'm in" is its one flag.
 *
 * An answer can be changed until the outing is booked or called off — the
 * server says so with a 409, which this page renders as a locked-in state
 * rather than an error.
 */

function formatTeeTime(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  const day = new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(date)
  const time = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
  return `${day} · ${time}`
}

export function InviteScreen({ token }: { token: string }) {
  usePageTitle('An invitation')

  const [invite, setInvite] = useState<InviteView | null>(null)
  const [dead, setDead] = useState(false)
  const [locked, setLocked] = useState(false)
  // WHICH answer is in flight, not merely that one is. The first version of
  // this page only disabled both buttons, so pressing "I'm in" looked like
  // nothing had happened until the response came back — the invitee's actual
  // complaint. The pressed button now says what it is doing.
  const [sending, setSending] = useState<InviteResponseChoice | null>(null)
  // Set when THIS visit recorded the answer, as opposed to arriving at a page
  // that already showed one. It earns the "just now" line and the receipt note.
  const [justAnswered, setJustAnswered] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const busy = sending !== null

  useEffect(() => {
    let active = true
    api
      .get<InviteViewResponse>(`/golf/invites/${encodeURIComponent(token)}`)
      .then((data) => {
        if (active) setInvite(data.invite)
      })
      .catch(() => {
        // 404 and everything else read the same to the invitee: no live
        // invitation behind this link.
        if (active) setDead(true)
      })
    return () => {
      active = false
    }
  }, [token])

  const respond = async (choice: InviteResponseChoice) => {
    setSending(choice)
    setError(null)
    try {
      const data = await api.post<InviteViewResponse>(
        `/golf/invites/${encodeURIComponent(token)}/respond`,
        { response: choice },
      )
      // Defensive merge: keep the outing details if the respond envelope is thin.
      setInvite((prev) =>
        prev ? { ...prev, ...data.invite, outing: data.invite.outing ?? prev.outing } : data.invite,
      )
      setJustAnswered(true)
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setLocked(true)
      } else {
        setError(e instanceof Error ? e.message : 'That answer did not go through — try again.')
      }
    } finally {
      setSending(null)
    }
  }

  return (
    <ClubPage chrome="invite">
      <Container className="max-w-[640px] pb-16 pt-16 text-center">
        {dead ? (
          <EmptyPlate
            title="That invitation isn't in the book."
            description="The link may have expired, or the outing it belonged to has come and gone. Ask your organizer for a fresh one — the course isn't going anywhere."
            action={
              <ClubLinkButton to="/" variant="ghost" size="sm">
                See the club
              </ClubLinkButton>
            }
          />
        ) : !invite ? (
          <p className="text-[14.5px] text-ink-soft">Fetching the invitation…</p>
        ) : (
          <>
            <Eyebrow>You are invited</Eyebrow>
            <h1 className="mb-3.5 mt-4 font-display text-[clamp(2rem,4.5vw,2.75rem)] font-medium leading-[1.06] tracking-[-0.014em] text-pine">
              {invite.outing.course_name}
            </h1>
            <p className="text-[15.5px] text-ink">
              {invite.outing.city} · {formatTeeTime(invite.outing.tee_off_at)}
            </p>
            <p className="mt-1.5 text-[13.5px] text-ink-soft">
              A party of {invite.outing.slots}
              {invite.outing.organizer_name ? `, arranged by ${invite.outing.organizer_name}` : ''} ·{' '}
              {invite.outing.accepted_count} of {invite.outing.slots} in so far
            </p>

            {locked ? (
              <div className="mt-8">
                <p className="font-display text-[19px] text-pine">This outing is locked in.</p>
                <p className="mt-2 text-[13.5px] text-ink-soft">
                  The tee time has been settled, so answers are closed
                  {invite.status === 'accepted'
                    ? ' — and yours was yes, so bring your clubs.'
                    : invite.status === 'declined'
                      ? ' — your regrets stand.'
                      : '.'}
                </p>
              </div>
            ) : invite.status === 'invited' ? (
              <div className="mt-8 flex flex-wrap justify-center gap-3.5">
                <ClubButton onClick={() => respond('accepted')} disabled={busy}>
                  {sending === 'accepted' ? 'Putting you down…' : 'I’m in'}
                </ClubButton>
                <ClubButton variant="ghost" onClick={() => respond('declined')} disabled={busy}>
                  {sending === 'declined' ? 'Sending regrets…' : 'Can’t make it'}
                </ClubButton>
              </div>
            ) : invite.status === 'accepted' ? (
              <div className="mt-8">
                <p className="font-display text-[19px] text-fairway">
                  You&rsquo;re in{justAnswered ? ' — that’s recorded' : ''}.
                </p>
                <p className="mt-2 text-[13.5px] text-ink-soft">
                  {justAnswered
                    ? `${invite.outing.organizer_name ?? 'The organizer'} can see it, and a confirmation is on its way to your inbox.`
                    : `${invite.outing.organizer_name ?? 'The organizer'} can see your answer.`}
                </p>
                {invite.share_cents !== null && (
                  <p className="mt-2 text-[13.5px] text-ink-soft">
                    Your share is{' '}
                    <span className="font-medium text-ink">{formatCents(invite.share_cents)}</span>.
                    Nothing to pay yet — you&rsquo;ll get a note once the tee time is booked.
                  </p>
                )}
                <p className="mt-3.5 text-[13.5px] text-ink-soft">Changed plans?</p>
                <ClubButton
                  variant="ghost"
                  size="sm"
                  className="mt-3.5"
                  onClick={() => respond('declined')}
                  disabled={busy}
                >
                  {sending === 'declined' ? 'Sending regrets…' : 'Can’t make it after all'}
                </ClubButton>
              </div>
            ) : (
              <div className="mt-8">
                <p className="font-display text-[19px] text-pine">
                  Your regrets are {justAnswered ? 'recorded' : 'sent'}.
                </p>
                <p className="mt-2 text-[13.5px] text-ink-soft">
                  No hard feelings — nothing is owed, and the seat is still yours if the day frees
                  up.
                </p>
                <ClubButton
                  variant="ghost"
                  size="sm"
                  className="mt-3.5"
                  onClick={() => respond('accepted')}
                  disabled={busy}
                >
                  Actually, I&rsquo;m in
                </ClubButton>
              </div>
            )}

            {error && (
              <p role="alert" className="mt-5 text-[12.5px] text-flag">
                {error}
              </p>
            )}
          </>
        )}
      </Container>
    </ClubPage>
  )
}
